import {frequencySchema} from "@indebted/payment-arrangement/Slider/Frequency"
import {z} from "zod"
import {queryOptions, useQueryClient} from "@tanstack/react-query"
import {useTranslation} from "react-i18next"
import {stripeQuery} from "@indebted/stripe-payment-arrangement/PaymentMethodForm"

import {getJson} from "../http"

// Zod schema for PaymentFormPageResponse validation
const PaymentFormResponseSchema = z.object({
	Locale: z.string(),
	HidePostalCode: z.boolean(),
	AcceptedCards: z.array(z.enum(["mastercard", "visa"])),
	PaymentArrangementID: z.string(),
	PublishableKey: z.string(),
	ClientSecret: z.string(),
	ProviderSetupID: z.string(),
	CustomerCountryISOAlpha: z.string(),
	PaymentArrangementFrequency: frequencySchema,
	PaymentArrangementNumberOfInstalments: z.number(),
	PaymentArrangementInstalmentAmount: z.number(),
	PaymentArrangementInstalmentCurrency: z.string(),
	PaymentArrangementStartDate: z.coerce.date(),
	PaymentArrangementEndDate: z.coerce.date(),
	RequestESIGNConsent: z.boolean(),
	RequiresEFTADisclosure: z.boolean(),
	EFTAPage: z.string(),
	type: z.literal("value"),
})

const NextResponseSchema = z.object({
	Next: z.string(),
	type: z.literal("redirect"),
})

const PaymentFormPageResponseSchema = z.preprocess(
	(data: any) => {
		if (data.Next) {
			return {Next: data.Next, type: "redirect"}
		}
		return {type: "value", ...data}
	},
	z.discriminatedUnion("type", [PaymentFormResponseSchema, NextResponseSchema]),
)

// Derive types from Zod schemas
export type PaymentFormResponse = z.infer<typeof PaymentFormResponseSchema>
export type PaymentFormPageResponse = z.infer<typeof PaymentFormPageResponseSchema>

// Main API function
export async function getPaymentFormPage({
	paymentArrangementID,
}: {
	paymentArrangementID: string
}): Promise<PaymentFormPageResponse> {
	return await getJson(
		`/app/payment-arrangements/${paymentArrangementID}/payment-form`,
		PaymentFormPageResponseSchema,
	)
}

export const usePaymentFormPageQuery = (paymentArrangementID: string) => {
	const queryClient = useQueryClient()
	const {t} = useTranslation()
	return queryOptions({
		queryKey: ["payment-form-page", paymentArrangementID],
		queryFn: async () => {
			const result = await getPaymentFormPage({paymentArrangementID})
			if (result.type == "value") {
				queryClient.prefetchQuery(stripeQuery(result.PublishableKey, t))
			}
			return result
		},
		gcTime: 0,
		refetchOnWindowFocus: false,
	})
}
